// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { AttendanceVerificationMethod } from "@clipboard-health/contract-backend-main";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { ClockOutChartingReminder } from "@src/appV2/redesign/Charting/ClockOutChartingReminder";
import { formatDateRangeDuration } from "@src/appV2/redesign/utils/formatDateRangeDuration";
import {
  getWorkerShiftPath,
  type GetWorkerShiftResponse,
} from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import {
  SHIFT_DETAILS_FULL_PATH,
  SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH,
} from "@src/appV2/redesign/WorkerShifts/path";
import { type NfcTagRequest } from "@src/appV2/Shifts/Shift/types";
import { useGetShiftFeedbackClockOutConfig } from "@src/appV2/Shifts/Shift/useGetShiftFeedbackClockOutConfig";
import { useQueryClient } from "@tanstack/react-query";
import { differenceInHours } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { useSkippableWait } from "../../../utils/useSkippableWait";
import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../../api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  ShiftStage,
  useRecordShiftTimekeepingAction,
} from "../../api/useRecordShiftTimekeepingAction";
import { NfcHashValidationAction } from "../NfcValidation/api/types";
import { useDisableShiftNfcCheck } from "../NfcValidation/api/useDisableShiftNfcCheck";
import { WorkerNfcValidation } from "../NfcValidation/WorkerNfcValidation";
import { ClockOutConfirmation } from "./ClockOutConfirmation";
import { DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS } from "./constants";
import { DisableShiftNfcFailed } from "./DisableShiftNfcFailed";
import { EarlyClockOutConfirmation } from "./EarlyClockOutConfirmation";
import { TimekeepingActionSuccessContent } from "./TimekeepingActionSuccessContent";

export interface ClockOutBottomSheetProps {
  modalState: UseModalState;
  shiftId: string;
  shiftEndDate: Date;
  workerId: string;
  workplaceId: string;
  workplaceName: string;
  isNfcClockOutEnabled: boolean;
  nfcTagRequests: NfcTagRequest[];
  shiftDuration: number;
  currentDate: Date;
  isMissedPunchRequestEnabled: boolean;
  onTimekeepingActionCompleted: (props?: { nonInstantPayReason?: string }) => void;
}

type ClockOutBottomSheetStep =
  | "charting-reminder"
  | "clock-out-confirm"
  | "nfc-validation"
  | "requesting-clock-out"
  | "requesting-non-instant-pay"
  | "disable-shift-nfc-check-failed"
  | "clock-out-success";

const MINIMUM_SHIFT_DURATION_FOR_EARLY_CLOCK_OUT_IN_HOURS = 4;
const MINIMUM_HOURS_BEFORE_END_FOR_EARLY_CLOCK_OUT_IN_HOURS = 1;

export function ClockOutBottomSheet(props: ClockOutBottomSheetProps) {
  const {
    modalState,
    shiftId,
    shiftEndDate,
    workerId,
    workplaceId,
    workplaceName,
    isNfcClockOutEnabled: isNfcClockOutEnabledProperty,
    nfcTagRequests,
    shiftDuration,
    currentDate,
    isMissedPunchRequestEnabled,
    onTimekeepingActionCompleted,
  } = props;

  const history = useHistory();
  const chartingReminderModalState = useModalState();
  const { chartingRecallEnabled: shouldShowChartingReminder } =
    useGetShiftFeedbackClockOutConfig(workplaceId);

  const firstStep: ClockOutBottomSheetStep = shouldShowChartingReminder
    ? "charting-reminder"
    : "clock-out-confirm";

  const [step, setStep] = useState<ClockOutBottomSheetStep>(firstStep);

  useEffect(() => {
    if (shouldShowChartingReminder) {
      setStep((currentStep) =>
        currentStep === "clock-out-confirm" ? "charting-reminder" : currentStep
      );
    }
  }, [shouldShowChartingReminder]);

  useEffect(() => {
    if (modalState.modalIsOpen && step === "charting-reminder") {
      chartingReminderModalState.openModal();
    }
  }, [modalState.modalIsOpen, step, chartingReminderModalState]);

  const { mutateAsync: recordTimekeepingAction } = useRecordShiftTimekeepingAction();
  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();
  const { mutateAsync: callDisableShiftNfcCheck } = useDisableShiftNfcCheck();

  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const { wait, skip: skipWait } = useSkippableWait();

  const [isNfcClockOutEnabled, setIsNfcClockOutEnabled] = useState(isNfcClockOutEnabledProperty);

  const shiftDetailsPath = generatePath(SHIFT_DETAILS_FULL_PATH, {
    shiftId,
  });
  const submitTimesheetPath = `${shiftDetailsPath}${SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH}`;

  const hoursBeforeEnd = differenceInHours(shiftEndDate, currentDate);
  const formattedHoursBeforeEnd = formatDateRangeDuration({
    startDate: currentDate,
    endDate: shiftEndDate,
  });

  const isEarlyClockOut =
    shiftDuration >= MINIMUM_SHIFT_DURATION_FOR_EARLY_CLOCK_OUT_IN_HOURS &&
    hoursBeforeEnd > MINIMUM_HOURS_BEFORE_END_FOR_EARLY_CLOCK_OUT_IN_HOURS;

  const closeBottomSheet = useCallback(() => {
    skipWait();
    modalState.closeModal();
    chartingReminderModalState.closeModal();
    setStep(firstStep);
  }, [modalState, chartingReminderModalState, skipWait, firstStep]);

  const onClockOutSuccess = useCallback(async () => {
    onTimekeepingActionCompleted();
    setStep("clock-out-success");
    await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);
    closeBottomSheet();
  }, [closeBottomSheet, wait, onTimekeepingActionCompleted]);

  const recordClockOut = useCallback(async () => {
    try {
      const timekeepingData = await recordTimekeepingAction({
        shiftId,
        stage: ShiftStage.CLOCK_OUT,
        shiftActionCheck: isNfcClockOutEnabled ? ActionCheckType.NFC : ActionCheckType.LOCATION,
        verificationMethod: isNfcClockOutEnabled
          ? AttendanceVerificationMethod.NFC_TAG
          : AttendanceVerificationMethod.GEOFENCE,
      });
      // This must be called before updating state bellow to preserve the correct order of actions
      await onClockOutSuccess();

      // Manually set shift as clocked out before the API GET call is made
      queryClient.setQueryData<GetWorkerShiftResponse>([getWorkerShiftPath(shiftId)], (previous) =>
        previous
          ? {
              ...previous,
              response: {
                ...previous.response,
                clockInOut: timekeepingData.clockInOut,
              },
            }
          : undefined
      );

      history.replace(submitTimesheetPath, { clockedOut: true });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock out, please try again.";

      logEvent(APP_V2_USER_EVENTS.CLOCK_OUT_FAILED, {
        shiftId,
        workerId,
        workplaceId,
        clockOutType: isNfcClockOutEnabled ? "nfc" : "geofence",
        error: errorMessage,
      });

      showErrorToast(errorMessage);
      closeBottomSheet();
    }
  }, [
    closeBottomSheet,
    history,
    isNfcClockOutEnabled,
    onClockOutSuccess,
    recordTimekeepingAction,
    shiftId,
    showErrorToast,
    submitTimesheetPath,
    queryClient,
    workerId,
    workplaceId,
  ]);

  const convertToNonInstantPay = useCallback(async () => {
    try {
      await markNonInstant({
        shiftId,
        reason: isNfcClockOutEnabled
          ? ShiftMarkedNonIpReasons.NFC_FAILURE
          : ShiftMarkedNonIpReasons.NO_LOCATION,
      });

      onTimekeepingActionCompleted({
        nonInstantPayReason: isNfcClockOutEnabled ? "NFC_FAILURE" : "NO_LOCATION",
      });
      modalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      closeBottomSheet();
    }
  }, [
    closeBottomSheet,
    isNfcClockOutEnabled,
    markNonInstant,
    modalState,
    shiftId,
    showErrorToast,
    onTimekeepingActionCompleted,
  ]);

  const disableShiftNfcCheck = useCallback(async () => {
    setStep("requesting-clock-out");

    try {
      await callDisableShiftNfcCheck({ shiftId, workerId });
      setIsNfcClockOutEnabled(false);
    } catch {
      setStep("disable-shift-nfc-check-failed");
      return;
    }

    // After disabling NFC, proceed directly to clock out
    await recordClockOut();
  }, [callDisableShiftNfcCheck, shiftId, workerId, recordClockOut]);

  return (
    <>
      {step === "charting-reminder" && shouldShowChartingReminder && (
        <ClockOutChartingReminder
          modalState={chartingReminderModalState}
          shiftId={shiftId}
          workplaceId={workplaceId}
          onConfirmClockOut={() => {
            setStep("clock-out-confirm");
            chartingReminderModalState.closeModal();
          }}
          onCancel={() => {
            closeBottomSheet();
          }}
        />
      )}

      {step === "clock-out-confirm" && isEarlyClockOut && (
        <EarlyClockOutConfirmation
          modalState={modalState}
          formattedDurationBeforeEnd={formattedHoursBeforeEnd}
          onConfirm={async () => {
            // Skip location check - go directly to NFC validation or clock out
            if (isNfcClockOutEnabled) {
              setStep("nfc-validation");
            } else {
              setStep("requesting-clock-out");
              await recordClockOut();
            }
          }}
          onCancel={closeBottomSheet}
        />
      )}

      {step === "clock-out-confirm" && !isEarlyClockOut && (
        <ClockOutConfirmation
          modalState={modalState}
          onConfirm={async () => {
            // Skip location check - go directly to NFC validation or clock out
            if (isNfcClockOutEnabled) {
              setStep("nfc-validation");
            } else {
              setStep("requesting-clock-out");
              await recordClockOut();
            }
          }}
          onCancel={closeBottomSheet}
        />
      )}

      {step !== "clock-out-confirm" && (
        <BottomSheet modalState={modalState} canDismiss={false}>
          {step === "nfc-validation" && (
            <WorkerNfcValidation
              isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
              shiftId={shiftId}
              metaProps={{ workerId, workplaceId, workplaceName }}
              nfcTagRequests={nfcTagRequests}
              clockAction={NfcHashValidationAction.CLOCK_OUT}
              onSuccess={async () => {
                setStep("requesting-clock-out");
                await recordClockOut();
              }}
              onSkipLocationCheck={async () => {
                setStep("requesting-non-instant-pay");
                await convertToNonInstantPay();
              }}
              onCancel={closeBottomSheet}
              onNfcNotSupported={disableShiftNfcCheck}
            />
          )}
          {step === "requesting-clock-out" && (
            <BottomSheetLoadingContent
              title="Confirming your clock out"
              description="Should only take a few seconds"
            />
          )}
          {step === "clock-out-success" && (
            <TimekeepingActionSuccessContent
              title="Clocked out"
              description="We hope you had a great shift!"
              onClose={closeBottomSheet}
            />
          )}
          {step === "requesting-non-instant-pay" && (
            <BottomSheetLoadingContent
              title="Converting to non-instant pay"
              description="Should only take a few seconds"
            />
          )}
          {step === "disable-shift-nfc-check-failed" && (
            <DisableShiftNfcFailed
              onTryAgain={async () => {
                await disableShiftNfcCheck();
              }}
              onClose={closeBottomSheet}
            />
          )}
        </BottomSheet>
      )}
    </>
  );
}
